import { Tabs, Tag } from 'antd';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import {
  getInProgressEStampAgreementsThunk,
  getPendingEStampAgreementsThunk,
  getUploadedEStampAgreementsThunk,
} from '../../../services/agreementService/agreementThunk';
import { AppDispatch, RootState } from '../../../store/store';
import InProgressEStampTab from './InProgressEStampTab';
import PendingEStampTab from './PendingEStampTab';
import UploadedEStampTab from './UploadedEStampTab';

import type { TabsProps } from 'antd';
import { REFINANCE_SEARCH_TYPE } from '../../../enum/refinanceSearchType';

const RefinanceEStampListingPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab');
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      sortOrder: activeTab === 'uploadedEStamp' ? 'asc' : 'desc',
      sortField: activeTab === 'uploadedEStamp' ? 'approveEStampingCertDate' : 'createdAt',
      search: {},
      filters: {
        application: {
          refinance: REFINANCE_SEARCH_TYPE.ONLY,
        },
      },
    }),
    []
  );

  const { pendingEStampAgreements, inProgressEStampAgreements, uploadedEStampAgreements } =
    useSelector((state: RootState) => state.agreement);

  const items: TabsProps['items'] = [
    {
      key: 'pendingEStamp',
      label: (
        <span>
          Pending Stamp Certificate{' '}
          {pendingEStampAgreements?.data?.searchTotal != null && (
            <Tag>{pendingEStampAgreements.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <PendingEStampTab />,
    },
    {
      key: 'inProgess',
      label: (
        <span>
          In Progress{' '}
          {inProgressEStampAgreements?.data?.searchTotal != null && (
            <Tag>{inProgressEStampAgreements.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <InProgressEStampTab />,
    },
    {
      key: 'uploadedEStamp',
      label: (
        <span>
          Uploaded Stamp Certificate{' '}
          {uploadedEStampAgreements?.data?.searchTotal != null && (
            <Tag>{uploadedEStampAgreements.data.searchTotal}</Tag>
          )}
        </span>
      ),
      children: <UploadedEStampTab />,
    },
  ];

  const fetchPendingAgreements = async (params?: any) => {
    await dispatch(
      getPendingEStampAgreementsThunk({
        ...params,
        companyId: process.env.REACT_APP_COMPANY_ID,
      })
    );
  };

  const fetchInProgressAgreements = async (params?: any) => {
    await dispatch(
      getInProgressEStampAgreementsThunk({
        ...params,
        companyId: process.env.REACT_APP_COMPANY_ID,
      })
    );
  };

  const fetchSignedAgreements = async (params?: any) => {
    await dispatch(
      getUploadedEStampAgreementsThunk({
        ...params,
        companyId: process.env.REACT_APP_COMPANY_ID,
      })
    );
  };

  const onTabChange = (key: string) => {
    const params = {
      ...initialParams,
      sortOrder: key === 'uploadedEStamp' ? 'asc' : 'desc',
      sortField: key === 'uploadedEStamp' ? 'approveEStampingCertDate' : 'createdAt',
    };

    if (key === 'pendingEStamp') {
      fetchPendingAgreements(params);
    } else if (key === 'inProgess') {
      fetchInProgressAgreements(params);
    } else if (key === 'uploadedEStamp') {
      fetchSignedAgreements(params);
    }

    queryParams.set('tab', key);
    queryParams.set('pageNum', `${initialParams.pagination.current}`);
    queryParams.set('pageSize', `${initialParams.pagination.pageSize}`);

    const newSearch = queryParams.toString();

    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  useLifecycles(() => {
    const params = {
      ...initialParams,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
    };

    fetchPendingAgreements(params);
    fetchInProgressAgreements(params);
    fetchSignedAgreements(params);
  });

  return (
    <>
      <PageHeader className="px-0 mb-2" title="Stamp Certificate Listing" />
      <Tabs
        defaultActiveKey={activeTab ?? 'pendingEStamp'}
        items={items}
        onChange={(key) => onTabChange(key)}
        destroyInactiveTabPane
      />
    </>
  );
};

export default RefinanceEStampListingPage;
